<template>
  <div class="key-points-block">
    <div class="key-points-header">
      <q-icon name="bolt" size="18px" :style="{ color: 'var(--oa-gold-full)' }" />
      <p class="key-points-title">{{ displayTitle }}</p>
    </div>
    <ul class="key-points-list">
      <li
        v-for="(point, idx) in cleanedPoints"
        :key="idx"
        class="key-point-item"
      >
        <span class="key-point-bullet" aria-hidden="true">
          <span class="bullet-dot"></span>
        </span>
        <span class="key-point-text" v-html="point" />
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { renderMarkdown } from '../markdown';

interface KeyPointsBlock {
  type: 'key_points';
  title?: string;
  points: string[];
}

const props = defineProps<{ block: KeyPointsBlock }>();

const displayTitle = computed(() => props.block.title || 'Puntos clave');

const cleanedPoints = computed(() => {
  return props.block.points.map((raw) => {
    let text = raw.trim();
    text = text.replace(/^[-*+]\s+/, '');
    text = text.replace(/^\d+[.)]\s+/, '');
    const html = renderMarkdown(text);
    return html.replace(/^<p>/, '').replace(/<\/p>\s*$/, '');
  });
});
</script>

<style scoped lang="scss">
.key-points-block {
  background: linear-gradient(
    135deg,
    color-mix(in srgb, var(--oa-gold-full) 6%, var(--oa-bg-2)) 0%,
    color-mix(in srgb, var(--oa-gold-full) 3%, var(--oa-bg-2)) 100%
  );
  border: 1px solid color-mix(in srgb, var(--oa-gold-full) 25%, transparent);
  border-radius: var(--oa-radius-xl);
  padding: 20px 24px;
  margin: 28px 0;
  box-shadow: 0 1px 3px color-mix(in srgb, var(--oa-gold-full) 8%, transparent);
}

.key-points-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid color-mix(in srgb, var(--oa-gold-full) 18%, transparent);
}

.key-points-title {
  font-weight: 700;
  font-size: var(--oa-text-sm);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--oa-gold-full);
  margin: 0;
}

.key-points-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.key-point-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: var(--oa-text-base);
  color: var(--oa-text-primary);
  line-height: 1.65;
}

.key-point-bullet {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 24px;
  flex-shrink: 0;
}

.bullet-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--oa-gold-full);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--oa-gold-full) 18%, transparent);
}

.key-point-text {
  flex: 1;
  min-width: 0;

  :deep(strong) {
    color: var(--oa-text-primary);
    font-weight: 700;
  }

  :deep(em) {
    font-style: italic;
  }

  :deep(code) {
    background: var(--oa-bg-3);
    padding: 1px 5px;
    border-radius: var(--oa-radius-sm);
    font-family: var(--oa-font-mono);
    font-size: 0.85em;
  }

  :deep(a) {
    color: var(--oa-gold-full);
    text-decoration: underline;
  }
}
</style>
